/**
 * Display-only sanitizer for project names imported from Word/Drive.
 *
 * Background: a chunk of imported project names carry a stray U+0096 (and a
 * lone U+0092) — these are Windows-1252 "smart" punctuation (0x96 = en dash,
 * 0x92 = right single quote) that got read as raw bytes instead of decoded,
 * so they landed in the DB as bare C1 control codepoints with no glyph. They
 * render as "tofu" boxes (□) mid-name, e.g. "Arlington ISD □ Atherton ES".
 * (Verified against prod: 111×U+0096, 1×U+0092 across 93/308 names.)
 *
 * This fixes the DISPLAY only — it never mutates stored data. We strip a small
 * set of glyph-less / replacement-prone codepoints:
 *   - U+FFFD replacement char
 *   - C0 controls (U+0000–U+001F, excl. tab/newline) and DEL (U+007F)
 *   - C1 controls (U+0080–U+009F)  ← the actual culprit here (U+0096/U+0092)
 *   - private-use area (U+E000–U+F8FF), a common source of unsupported tofu
 * An occurrence between two word characters becomes " — " (em dash + spaces)
 * so the original separator intent is preserved; an occurrence at an edge (or
 * adjacent to existing spaces) collapses to nothing. Result is whitespace-
 * tidied. Pure-ASCII names (the common case) pass straight through untouched.
 */
// Matches one-or-more consecutive "tofu"-prone codepoints (see doc above).
const TOFU_RE = /[\uFFFD\u0000-\u0008\u000B-\u000C\u000E-\u001F\u007F\u0080-\u009F\uE000-\uF8FF]+/g;
export function sanitizeProjectName(raw?: string | null): string {
  if (!raw) return raw ?? '';
  // Fast path: nothing offending → return as-is (no allocation churn).
  if (!TOFU_RE.test(raw)) return raw;
  TOFU_RE.lastIndex = 0; // reset: .test() advances lastIndex on a global regex
  // Restore Windows-1252 smart punctuation that was stored as bare C1 bytes
  // (0x80–0x9F) — e.g. 0x96 → en dash, 0x92 → apostrophe, 0x93/94 → curly quotes,
  // 0x85 → ellipsis — so "Parkhill JH □ Additions" → "Parkhill JH – Additions"
  // and "Owner□s" → "Owner's", instead of bluntly dashing them out.
  const C1_MAP: Record<number, string> = {
    0x82: '‚',
    0x83: 'ƒ',
    0x84: '„',
    0x85: '…',
    0x86: '†',
    0x87: '‡',
    0x88: 'ˆ',
    0x89: '‰',
    0x8a: 'Š',
    0x8b: '‹',
    0x8c: 'Œ',
    0x8e: 'Ž',
    0x91: '‘',
    0x92: '’',
    0x93: '“',
    0x94: '”',
    0x95: '•',
    0x96: '–',
    0x97: '—',
    0x98: '˜',
    0x99: '™',
    0x9a: 'š',
    0x9b: '›',
    0x9c: 'œ',
    0x9e: 'ž',
    0x9f: 'Ÿ',
  };
  const restored = raw.replace(/[-]/g, (ch) => C1_MAP[ch.charCodeAt(0)] ?? ch);
  const cleaned = restored.replace(TOFU_RE, (_m, offset: number, str: string) => {
    const prev = str[offset - 1];
    const next = str[offset + _m.length];
    // Inter-word (real chars on both sides, neither already a space) → em-dash.
    if (prev && next && !/\s/.test(prev) && !/\s/.test(next)) return ' — ';
    // Edge or space-adjacent → drop it; surrounding spaces are tidied below.
    return ' ';
  });
  // Collapse any doubled spaces we introduced and trim the ends.
  return cleaned.replace(/\s{2,}/g, ' ').trim();
}
